import IconButton from "shared/components/forms/buttons/IconButton";
import { useMultistepForm } from "shared/hooks/useMultistepForm";
import { FormEvent, useEffect, useRef, useState } from "react";
import Loader from "shared/components/common/Loader";
import { useUserDetailsStore } from "shared/stores/userDetailsStore";
import { UserDetails } from "shared/types/UserDetailsTypes";
import { userDetailsMock } from "shared/constants/mocks/userDetails";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PasswordManagerFormData } from "../types";
import "styles/flows/forex/passwordManager/passwordManager.css";
import { useSharedStore } from "shared/stores/sharedStore";
import ReCAPTCHA from "react-google-recaptcha";
import {
  getHostByUrl,
  GetMarketingInfo,
  getUrlFromRedirect,
} from "shared/utils";
import { showNotification } from "shared/components/common/Notifications";
import { useDeviceStore } from "shared/stores/deviceStore";
import { WidgetPostMessageType } from "shared/types/WidgetPostMessageType";
import PasswordManagerStep from "./PasswordManagerStep";
import PasswordSetupStep from "./PasswordSetupStep";
import SecurityAuthStep from "./SecurityAuthStep";
import { passwordStrengthItems } from "../constants";

const PasswordManager = () => {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const { t } = useTranslation();
  const userDetails = useUserDetailsStore((state) => state.userDetails);
  const domain = useSharedStore((state) => state.serverDomain);
  const isMobile = useDeviceStore((state) => state.isMobile);

  const updateUserDetails = useUserDetailsStore(
    (state) => state.updateUserDetails
  );

  const [formData, setFormData] = useState<PasswordManagerFormData>({
    account: { name: "", value: "" },
    password: "",
    reenteredPassword: "",
    otpValue: "",
    activeTab: "sms",
    isOtpSent: { email: 0, sms: 0 },
    strengthItems: passwordStrengthItems,
    currentPassword: undefined,
  });

  const [formValid, setFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isMetaPasswordChange, setIsMetaPasswordChange] = useState(false);

  const [searchParams] = useSearchParams();
  const toStage = searchParams.get("toStage");

  const isFrontEgg = searchParams.get("frontegg")?.toLowerCase() === "true";

  // // Sending Widget Loaded event once component mounts
  // useEffect(() => {
  //   sendPostMessage({
  //     eventType: "WIDGET_LOADED",
  //     widgetName: "PasswordManagerWidget",
  //     payload: { status: "loaded" },
  //   });
  // }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<WidgetPostMessageType>) => {
      if (
        event.data.eventType === "AUTO_GENERATED_PASSWORD" &&
        event.data.widgetName === "PasswordManagerWidget" &&
        !formData.currentPassword &&
        event.data.payload.credential?.password
      ) {
        handleInputChange(
          "currentPassword",
          event.data.payload.credential.password
        );
      }
    };

    // Listen for incoming messages
    window.addEventListener("message", handleMessage);

    // Notify host that the widget has fully loaded and is ready to receive messages
    window.parent.postMessage(
      {
        eventType: "WIDGET_LOADED",
        widgetName: "PasswordManagerWidget",
        payload: { status: "loaded" },
      },
      "*"
    );

    return () => window.removeEventListener("message", handleMessage);
  }, [formData.currentPassword, handleInputChange]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const url = `${
          document.referrer
        }SERVER/en/${domain}/Client/GetUserInfo?state=0&siteLanguge=en&getTradeInfo=false&time=${Date.now()}`;

        const response = await fetch(url, {
          method: "POST",
          credentials: "include",
        });

        if (!response.ok) {
          console.error("Failed to fetch user details");

          if (!import.meta.env.PROD) {
            updateUserDetails(userDetailsMock);
          } else {
            setIsError(true);
          }
          return;
        }
        const userDetails = (await response.json()) as UserDetails;

        if (!import.meta.env.PROD) {
          console.log(userDetails);

          if (!userDetails.Accounts) {
            updateUserDetails(userDetailsMock);
          }
        }
        updateUserDetails(userDetails);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [updateUserDetails, domain]);

  useEffect(() => {
    if (toStage === "2") {
      goTo(1);
    }
  }, [toStage]);

  function handleInputChange(id: string, value: unknown) {
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  }

  function onNextStep(goMeta: boolean = false) {
    if (goMeta) {
      handleInputChange("account", "");
      handleInputChange("password", "");
      handleInputChange("reenteredPassword", "");
      setFormValid(false);
    }

    setIsMetaPasswordChange(goMeta);
    next();
  }

  function onBackStep() {
    if (isMetaPasswordChange && currentStepIndex == 1) {
      handleInputChange("account", "");
      handleInputChange("password", "");
      handleInputChange("reenteredPassword", "");
      setFormValid(false);
      setIsMetaPasswordChange(false);
    }
    if (isFirstStep && window.parent) {
      const closePayload = {
        eventType: "CLOSE_WIDGET",
        widgetName: "PasswordManagerWidget",
        payload: { reason: "user_closed" },
      } as WidgetPostMessageType;

      window.parent.postMessage(closePayload, "*");

      console.log(`Post Message: ${JSON.stringify(closePayload)}`);
    } else {
      back();
    }
  }

  const changePassword = async (
    isDemoMetaAccount: boolean,
    isFrontEgg: boolean = false
  ): Promise<string> => {
    try {
      if (
        userDetails &&
        !userDetails.FirstName.includes(
          import.meta.env.VITE_RECAPTCHA_EXCLUDE_FIRSTNAME
        )
      ) {
        const token = await recaptchaRef.current?.executeAsync();

        if (!token) {
          console.error("Failed captcha");
          return "passChangeError-captcha";
        }
      } else {
        console.log("Skipping reCAPTCHA...");
      }

      setIsLoading(true);

      const formDataRequest = new FormData();
      const accountSelected = formData.account.value;
      const { screenSize, timeZone, timeZoneName, newDevice } =
        GetMarketingInfo();

      const MarketingInfoHidden = `isAppsInstall=false&isMobile=${isMobile}&Device=PC&ScreenRes=${screenSize}&timeZone=${timeZone}&timeZoneName=${timeZoneName}&newdevice=${newDevice}`;

      let SelectAccountDropDown = "SELECT AN ACCOUNT";
      let FormLogicHidden = "submitOtpCodeMain";

      if (accountSelected) {
        SelectAccountDropDown = accountSelected;
        FormLogicHidden = "submitOtpCodeMeta";
      }

      if (isDemoMetaAccount) {
        FormLogicHidden = "changePasswordDemoAccount";
      }

      formDataRequest.append("SelectAccountDropDown", SelectAccountDropDown);
      formDataRequest.append("Password", formData.password);
      formDataRequest.append("ReEnterPassword", formData.reenteredPassword);
      formDataRequest.append("FormLogicHidden", FormLogicHidden);
      formDataRequest.append("MarketingInfoHidden", MarketingInfoHidden);
      formDataRequest.append(
        "CountryCodeHidden",
        userDetails?.CountryCode || ""
      );
      formDataRequest.append("LcRefHidden", "");
      formDataRequest.append("LcExtraData", formData.activeTab);
      formDataRequest.append("WidgetRequestId", crypto.randomUUID());

      if (isFrontEgg && formData.currentPassword) {
        formDataRequest.append("CurrentPassword", formData.currentPassword);
      } else {
        if (formData.otpValue.length === 6) {
          for (let index = 0; index < formData.otpValue.length; index++) {
            formDataRequest.append(`Token${index}`, formData.otpValue[index]);
          }
        }
      }

      const formURL = `${document.referrer}SERVER/en/${domain}/Client/PartialForm?widgetName=PasswordManagerWidget&widgetLanguage=en&stage=3&isPopup=false`;
      const otpResponse = await fetch(formURL, {
        method: "POST",
        body: formDataRequest,
        credentials: "include",
      });

      if (otpResponse.ok) {
        const otpData = await otpResponse.text();
        console.log(otpData);
        return otpData;
      }

      return "";
    } catch (error) {
      console.error(error);
      return "";
    } finally {
      setIsLoading(false);
      recaptchaRef.current?.reset();
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let responseMessage = "";

    // For meta accounts don't need otp if its a demo account
    if (isMetaPasswordChange) {
      const isDemoMetaAccount = Boolean(
        currentStepIndex === 1 &&
          formData.account.value &&
          userDetails?.Accounts.find(
            (account) => account.Name === formData.account.value
          )?.IsDemo
      );

      if (!isLastStep && !isDemoMetaAccount) return next();

      responseMessage = await changePassword(isDemoMetaAccount);
    }
    // frontegg and main account
    else if (isFrontEgg) {
      responseMessage = await changePassword(false, isFrontEgg);
    }
    // For main accounts don't need otp if only demo accounts are in the account
    else {
      // const isAnyRealAccount =
      //   userDetails?.Accounts.findIndex((account) => !account.IsDemo) !== -1;

      // const isOnlyMainDemo = Boolean(
      //   currentStepIndex === 1 && !isAnyRealAccount
      // );

      // if (!isLastStep && !isOnlyMainDemo) return next();
      if (!isLastStep) return next();

      responseMessage = await changePassword(false);
    }

    switch (true) {
      case responseMessage.includes("passChangeError"): {
        if (responseMessage.includes("InvalidCode")) {
          showNotification({
            type: "error",
            title: t("PasswordManager.VerificationFailedTitle"),
            message: t("PasswordManager.VerificationFailedMessage"),
          });
          // frontegg - current password is wrong
        } else if (responseMessage.includes("UserPasswordWrong")) {
          showNotification({
            type: "error",
            title: t("PasswordManager.VerificationFailedTitle"),
            message: t("PasswordManager.Setup.CurrentPasswordFailedMessage"),
          });
        } else if (responseMessage.includes("PasswordPolicy")) {
          showNotification({
            type: "error",
            title: t("PasswordManager.VerificationFailedTitle"),
            message: t("PasswordManager.Setup.PolicyPasswordFailedMessage"),
          });
        } else {
          showNotification({
            type: "error",
            title: t("PasswordManager.GeneralErrorTitle"),
            message: t("PasswordManager.GeneralErrorMessage"),
          });
        }
        break;
      }
      case responseMessage.includes("UserBlock"): {
        showNotification({
          type: "error",
          title: t("PasswordManager.OTPUserBlockedTitle"),
          message: t("PasswordManager.OTPUserBlockedMessage"),
        });
        break;
      }
      case responseMessage.toLowerCase().includes("redirect"):
      case responseMessage === "passChanged": {
        if (window.parent) {
          var reason = responseMessage;

          if (responseMessage.toLowerCase().includes("redirect")) {
            reason = getUrlFromRedirect(responseMessage.toLowerCase());
          }
          const closePayload = {
            eventType: "CLOSE_WIDGET",
            widgetName: "PasswordManagerWidget",
            payload: {
              reason: reason,
              credential: {
                email: userDetails?.Email,
                password: formData.password,
              },
            },
          } as WidgetPostMessageType;

          window.parent.postMessage(closePayload, "*");
          console.log(`Post Message: ${JSON.stringify(closePayload)}`);
        }
        return;
      }

      default:
        showNotification({
          type: "error",
          title: t("PasswordManager.GeneralErrorTitle"),
          message: t("PasswordManager.GeneralErrorMessage"),
        });
        break;
    }
  };

  const { step, back, next, goTo, isLastStep, isFirstStep, currentStepIndex } =
    useMultistepForm([
      {
        title: "PasswordManager.Header",
        element: (
          <>{userDetails && <PasswordManagerStep next={onNextStep} />}</>
        ),
      },
      {
        title: "PasswordManager.Setup.Header",
        element: (
          <PasswordSetupStep
            formData={formData}
            handleInputChange={handleInputChange}
            formValid={formValid}
            setFormValid={setFormValid}
            showTradingAccounts={isMetaPasswordChange}
          />
        ),
      },
      {
        title: "PasswordManager.SecurityAuth.Header",
        element: (
          <SecurityAuthStep
            formData={formData}
            handleInputChange={handleInputChange}
          />
        ),
      },
    ]);

  if (isError) return <p>Error: {isError}</p>;

  if (!step) return null;

  return (
    <>
      <header className="header">
        <IconButton
          wrapperStyle={{
            visibility:
              getHostByUrl(document.referrer) === "web"
                ? isFirstStep
                  ? "hidden"
                  : "visible"
                : "visible",
          }}
          iconName="ArrowLeftIcon"
          id="back-button"
          onClickHandler={onBackStep}
        />
        <h1>{t(step.title)}</h1>
      </header>
      <form onSubmit={onSubmit}>
        {step.element}
        {/* Invisible reCAPTCHA, need to run as admin */}
        <div style={{ display: currentStepIndex > 0 ? "block" : "none" }}>
          <ReCAPTCHA
            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
            size="invisible"
            ref={recaptchaRef}
          />
        </div>
      </form>
      {isLoading && (
        <Loader isTransparent={true} transparentBackground={true} />
      )}
    </>
  );
};

export default PasswordManager;
